import {
    CONFERENCE_JOINED,
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO
} from '../base/conference/actionTypes';
import {
    GRANT_MODERATOR,
    KICK_PARTICIPANT,
    MUTE_REMOTE_PARTICIPANT,
    PARTICIPANT_JOINED
} from '../base/participants/actionTypes';
import { participantLeft, participantUpdated } from '../base/participants/actions';
import { getLocalParticipant, getParticipantDisplayName } from '../base/participants/functions';
import { MiddlewareRegistry } from '../base/redux';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../notifications';

import {
    areDeviceIndicatorsEnabled,
    getOtherSessionIdsOf,
    getParticipantSessions,
    getSessionPersistenceOptions,
    getStaleSessionsOf
} from './functions';
import logger from './logger';

/**
 * Moderator actions which address a person rather than one of the windows that
 * person happens to have open. Acting on any endpoint acts on all of them.
 *
 * @type {Array<string>}
 */
const PER_PERSON_ACTIONS = [
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
    GRANT_MODERATOR,
    KICK_PARTICIPANT,
    MUTE_REMOTE_PARTICIPANT
];

/**
 * Marks the copies this middleware dispatches, so that they are carried out
 * once instead of fanning out again.
 *
 * @type {string}
 */
const FANNED_OUT = '_sessionFanOut';

/**
 * Keeps one signed in person represented as one person in the conference, even
 * though every device, tab and reconnect is a separate endpoint.
 */
MiddlewareRegistry.register(store => next => action => {
    if (!action[FANNED_OUT] && action.id && PER_PERSON_ACTIONS.includes(action.type)) {
        return _applyToEveryDevice(store, next, action);
    }

    switch (action.type) {
    case CONFERENCE_JOINED: {
        const result = next(action);

        _identifyLocalParticipant(store);

        return result;
    }
    case PARTICIPANT_JOINED: {
        const { conference, participant } = action;
        const staleSessions = getStaleSessionsOf(store.getState(), participant);
        const result = next(action);

        // A reconnect of a client session produces a new endpoint id, so the
        // endpoint it replaces stays on screen until its unavailable presence
        // arrives, which after an unclean disconnect can take minutes or never
        // happen at all. Drop it as soon as its replacement is here.
        for (const stale of staleSessions) {
            logger.info(`Removing the stale endpoint ${stale.id} replaced by ${participant.id}`);
            store.dispatch(participantLeft(stale.id, conference ?? participant.conference));
        }

        if (!staleSessions.length && !participant.local
                && areDeviceIndicatorsEnabled(store.getState())) {
            _notifyAdditionalSession(store, participant);
        }

        return result;
    }
    }

    return next(action);
});

/**
 * Repeats a moderator action on the person's other devices. Muting, force
 * muting, granting moderator and removing all address the person: leaving their
 * second window unmuted or still in the room would defeat the point.
 *
 * @param {Object} store - The redux store.
 * @param {Function} next - The next middleware in the chain.
 * @param {Object} action - The moderator action.
 * @private
 * @returns {Object}
 */
function _applyToEveryDevice(store, next, action) {
    const otherIds = getOtherSessionIdsOf(store.getState(), action.id);
    const result = next(action);

    for (const id of otherIds) {
        store.dispatch({ ...action,
            id,
            [FANNED_OUT]: true });
    }

    return result;
}

/**
 * Labels the local participant with the same identity this endpoint advertises
 * in its presence, so that the local tile groups with the person's other
 * endpoints exactly like remote ones do.
 *
 * @param {Object} store - The redux store.
 * @private
 * @returns {void}
 */
function _identifyLocalParticipant({ dispatch, getState }) {
    const state = getState();
    const { deviceLabel, enabled, sessionId, userId } = getSessionPersistenceOptions(state);
    const localParticipant = getLocalParticipant(state);

    if (!enabled || !localParticipant) {
        return;
    }

    dispatch(participantUpdated({
        deviceLabel,
        id: localParticipant.id,
        local: true,
        persistentUserId: userId,
        sessionId
    }));
}

/**
 * Tells everyone that a participant which just joined is another device of a
 * person who is already in the conference, so that a second tile with the same
 * name does not read as a second person.
 *
 * @param {Object} store - The redux store.
 * @param {Object} participant - The participant which just joined.
 * @private
 * @returns {void}
 */
function _notifyAdditionalSession({ dispatch, getState }, participant) {
    const state = getState();
    const sessions = getParticipantSessions(state, participant.id);

    if (sessions.length < 2) {
        return;
    }

    dispatch(showNotification({
        descriptionArguments: {
            count: sessions.length,
            device: participant.deviceLabel,
            name: getParticipantDisplayName(state, participant.id)
        },
        descriptionKey: participant.deviceLabel
            ? 'sessionPersistence.joinedFromNamedDevice'
            : 'sessionPersistence.joinedFromAnotherDevice',
        titleKey: 'sessionPersistence.sameParticipantTitle'
    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
}
